import { NavigateFunction } from "react-router-dom";
import type { UrlParams, NavigationHelper } from "../types";

/**
 * 创建导航助手
 * @param navigate React Router的navigate函数
 * @returns 导航助手对象
 */
export const createNavigationHelper = (
  navigate: NavigateFunction
): NavigationHelper => {
  const buildUrl = (path: string, params?: UrlParams): string => {
    if (!params || Object.keys(params).length === 0) {
      return path;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  };

  return {
    goToTemplateList: (params?: UrlParams) => {
      const url = buildUrl("/templates", params);
      navigate(url);
    },

    goToTemplateDetail: (id: string, params?: UrlParams) => {
      const url = buildUrl(`/templates/${id}`, params);
      navigate(url);
    },

    goToTemplateCreate: (params?: UrlParams) => {
      const url = buildUrl("/templates/create", params);
      navigate(url);
    },

    goToTemplateEdit: (id: string, params?: UrlParams) => {
      const url = buildUrl(`/templates/${id}/edit`, params);
      navigate(url);
    },

    goBack: () => {
      navigate(-1);
    },
  };
};

/**
 * 解析URL参数
 * @param searchParams URLSearchParams对象
 * @returns 解析后的参数对象
 */
export const parseUrlParams = (searchParams: URLSearchParams): UrlParams => {
  const params: UrlParams = {};

  for (const [key, value] of searchParams.entries()) {
    // 尝试转换为数字
    if (/^\d+$/.test(value)) {
      params[key] = parseInt(value, 10);
    }
    // 尝试转换为布尔值
    else if (value === "true" || value === "false") {
      params[key] = value === "true";
    }
    // 保持字符串
    else {
      params[key] = value;
    }
  }

  return params;
};

/**
 * 获取当前路由的面包屑路径
 * @param pathname 当前路径
 * @param templateId 模板ID（可选）
 * @param templateName 模板名称（可选）
 * @returns 面包屑项数组
 */
export const getBreadcrumbItems = (
  pathname: string,
  templateId?: string,
  templateName?: string
) => {
  const items: Array<{ title: string; path?: string }> = [
    {
      title: "首页",
      path: "/",
    },
  ];

  if (pathname.startsWith("/templates")) {
    items.push({
      title: "模板管理",
      path: "/templates",
    });

    if (pathname.includes("/create")) {
      items.push({
        title: "创建模板",
      });
    } else if (templateId) {
      items.push({
        title: templateName || `模板 ${templateId}`,
        path: `/templates/${templateId}`,
      });

      if (pathname.includes("/edit")) {
        items.push({
          title: "编辑模板",
        });
      }
    }
  }

  return items;
};

/**
 * 检查路径是否匹配
 * @param pathname 当前路径
 * @param pattern 匹配模式
 * @returns 是否匹配
 */
export const matchPath = (pathname: string, pattern: string): boolean => {
  // 简单的路径匹配，支持参数占位符
  const patternRegex = pattern.replace(/:\w+/g, "[^/]+");
  const regex = new RegExp(`^${patternRegex}$`);
  return regex.test(pathname);
};

/**
 * 提取路径参数
 * @param pathname 当前路径
 * @param pattern 路径模式
 * @returns 参数对象
 */
export const extractPathParams = (
  pathname: string,
  pattern: string
): Record<string, string> => {
  const params: Record<string, string> = {};
  const patternParts = pattern.split("/");
  const pathParts = pathname.split("/");

  if (patternParts.length !== pathParts.length) {
    return params;
  }

  patternParts.forEach((part, index) => {
    if (part.startsWith(":")) {
      const paramName = part.slice(1);
      params[paramName] = pathParts[index];
    }
  });

  return params;
};

// 跳转到登录页
export const navigateToLogin = (isHash = false, backUrl?: string) => {
  // 判断是否有重定向链接
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get("redirectUrl");
  if (isHash) {
    window.location.hash =
      "#/login" + "?redirectUrl=" + (backUrl || window.location.pathname || "");
    return;
  } else {
    window.location.href =
      "/login" +
      "?redirectUrl=" +
      (backUrl
        ? backUrl
        : redirect
        ? decodeURIComponent(redirect)
        : window.location.pathname || "");
    return;
  }
};
